import type { ClassNameValue } from "tailwind-merge";
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "~/lib/ui/utils/utils";

const SelectBase = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

function SelectTrigger({
  children,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        `
          flex h-10 w-full items-center justify-between rounded-md border
          border-border bg-background-muted px-2.5 py-1 text-sm
          ring-offset-background transition-colors

          [&>span]:line-clamp-1

          disabled:cursor-not-allowed disabled:opacity-50

          focus-visible:outline-hidden focus-visible:border-foreground/40
          focus-visible:ring-0

          hover:bg-muted/40

          placeholder:text-muted-foreground
        `,
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

function SelectContent({
  children,
  className,
  position = "popper",
  hideScrollButtons = false,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  /**
   * Drop the chevron scroll buttons Radix mounts when the list overflows.
   * Wheel and keyboard scrolling still work; the list just scrolls plainly.
   */
  hideScrollButtons?: boolean;
}) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          `
            relative z-50 max-h-96 min-w-32 overflow-hidden rounded-lg border
            border-border/80 bg-popover text-popover-foreground shadow-lg
            ring-1 ring-black/[0.04] dark:ring-white/[0.06]

            data-[side=bottom]:slide-in-from-top-2

            data-[side=left]:slide-in-from-right-2

            data-[side=right]:slide-in-from-left-2

            data-[side=top]:slide-in-from-bottom-2

            data-[state=closed]:animate-out data-[state=closed]:fade-out-0
            data-[state=closed]:zoom-out-95

            data-[state=open]:animate-in data-[state=open]:fade-in-0
            data-[state=open]:zoom-in-95
          `,
          position === "popper" &&
            `
              data-[side=bottom]:translate-y-1

              data-[side=left]:-translate-x-1

              data-[side=right]:translate-x-1

              data-[side=top]:-translate-y-1
            `,
          className,
        )}
        position={position}
        {...props}
      >
        {!hideScrollButtons && <SelectScrollUpButton />}
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              `
                h-(--radix-select-trigger-height) w-full
                min-w-(--radix-select-trigger-width)
              `,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        {!hideScrollButtons && <SelectScrollDownButton />}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      className={cn(
        "py-1.5 pl-8 pr-2 text-xs font-semibold text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

function SelectItem({
  children,
  className,
  indicatorSide = "left",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & {
  /**
   * Which side of the text carries the check mark. "right" trails the check
   * after the label, so text lines up flush with the list's left padding.
   */
  indicatorSide?: "left" | "right";
}) {
  const indicator = (
    <span
      className={cn(
        "flex h-3.5 w-3.5 shrink-0 items-center justify-center",
        indicatorSide === "left" ? "mr-1" : "ml-auto",
      )}
    >
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5" />
      </SelectPrimitive.ItemIndicator>
    </span>
  );
  return (
    <SelectPrimitive.Item
      className={cn(
        `
          relative flex w-full cursor-default select-none items-center rounded-md
          py-1.5 pl-2 pr-2 text-[13px] outline-hidden transition-colors

          data-disabled:pointer-events-none data-disabled:opacity-50

          focus:bg-accent focus:text-accent-foreground
        `,
        indicatorSide === "right" && "gap-2",
        className,
      )}
      {...props}
    >
      {indicatorSide === "left" && indicator}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {indicatorSide === "right" && indicator}
    </SelectPrimitive.Item>
  );
}

type SelectProps = React.ComponentProps<typeof SelectBase> & {
  placeholder?: string;
  label?: string;
  id?: string;
  /** Suppress repeating `label` as a header inside the open dropdown list. */
  hideListLabel?: boolean;
  className?: ClassNameValue;
  options: { disabled?: boolean; value: string; label: string }[];
  /** Forwarded to the trigger button, not the Radix root. */
  ref?: React.ComponentProps<typeof SelectTrigger>["ref"];
};

function Select({
  className,
  id,
  label,
  hideListLabel,
  options,
  placeholder,
  ref,
  ...props
}: SelectProps) {
  const generatedId = React.useId();
  const triggerId = id ?? generatedId;

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label className="text-sm font-medium leading-none" htmlFor={triggerId}>
          {label}
        </label>
      )}
      <SelectBase {...props}>
        <SelectTrigger
          aria-label={label ?? placeholder}
          className={cn("w-full", label && "mt-[4px]")}
          id={triggerId}
          ref={ref}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {label && !hideListLabel && <SelectLabel>{label}</SelectLabel>}
            {options.map((option) => (
              <SelectItem
                disabled={option.disabled}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </SelectBase>
    </div>
  );
}

export {
  Select,
  SelectBase,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
};
